import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { setPreviewLanguage } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";
import { RubricResetButton } from "../shared/RubricResetButton";
import { PaiementPreviewData } from "../View";

import { PropsType, PaymentMethod, SplitOption } from "./types";
import {
    LANGUAGE_CODES,
    DEFAULT_PAYMENT_METHODS,
    DEFAULT_SPLIT_OPTIONS,
} from "./constants";
import { SCAN_TO_DEFAULT_MAIN_COLOR } from "../shared/constants";
import { Container, LeftSection } from "./styled";
import { PaymentMethodsCard } from "./PaymentMethodsCard";
import { SplitOptionsCard } from "./SplitOptionsCard";

// ─── Component ──────────────────────────────────────────

export function Paiement({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const { setSharedPaiementData } = React.useContext(ScanToContext);

    // ─── Languages ───────────────────────────────────────
    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // ─── State ──────────────────────────────────────────
    // Cast: PaymentConfig type doesn't declare every UI-state field used at
    // runtime (selectedLang, etc.). Out of scope for ARCH-02.
    const savedPayment = useSelector(
        (state: RootState) => state.scanTo?.paymentOptions
    ) as any;
    const [paymentEnabled, setPaymentEnabled] = React.useState(
        savedPayment?.paymentEnabled ?? true
    );
    // Editing + preview share ONE language: the shared previewLanguage, so the
    // payment popup follows the same locale (and RTL) as every other section.
    // Both pickers (methods + split) drive the shared language. Aliased so the
    // existing call sites keep working unchanged.
    const dispatch = useDispatch();
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );
    const selectedLang = previewLanguage;
    const selectedSplitLang = previewLanguage;
    const setSelectedLang = React.useCallback(
        (lang: string) => dispatch(setPreviewLanguage(lang)),
        [dispatch]
    );
    const setSelectedSplitLang = setSelectedLang;
    const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>(
        () =>
            savedPayment?.fullPaymentMethods ||
            JSON.parse(JSON.stringify(DEFAULT_PAYMENT_METHODS))
    );
    const [splitPaymentEnabled, setSplitPaymentEnabled] = React.useState(
        savedPayment?.splitPaymentEnabled ?? true
    );
    const [splitOptions, setSplitOptions] = React.useState<SplitOption[]>(
        () =>
            savedPayment?.fullSplitOptions ||
            JSON.parse(JSON.stringify(DEFAULT_SPLIT_OPTIONS))
    );

    const [methodDescVisible, setMethodDescVisible] = React.useState<
        Record<string, boolean>
    >(
        savedPayment?.methodDescVisible || {
            online: true,
            tpe: true,
            cash: true,
        }
    );
    const [splitDescVisible, setSplitDescVisible] = React.useState<
        Record<string, boolean>
    >(
        savedPayment?.splitDescVisible || {
            equal: true,
            by_item: true,
            custom: true,
        }
    );

    // Reset just the "modes of payment" section (payment methods) to defaults:
    // enable toggle, language, the methods list and their description toggles.
    const resetPaymentMethods = () => {
        setPaymentEnabled(true);
        setSelectedLang("fr");
        setPaymentMethods(JSON.parse(JSON.stringify(DEFAULT_PAYMENT_METHODS)));
        setMethodDescVisible({ online: true, tpe: true, cash: true });
    };

    // Reset just the "Split payment" section to defaults: enable toggle,
    // language, the split options and their description toggles.
    const resetSplitOptions = () => {
        setSplitPaymentEnabled(true);
        setSelectedSplitLang("fr");
        setSplitOptions(JSON.parse(JSON.stringify(DEFAULT_SPLIT_OPTIONS)));
        setSplitDescVisible({ equal: true, by_item: true, custom: true });
    };

    // Reset the WHOLE payment rubric to defaults (methods + split), directly on
    // local state (the broadcast effect then syncs Redux + preview).
    const resetPaymentAll = () => {
        resetPaymentMethods();
        resetSplitOptions();
    };

    // ─── Preview data builder ───────────────────────────
    const buildPreviewData = React.useCallback((): PaiementPreviewData => {
        return {
            paymentEnabled,
            paymentMethods: paymentMethods.map((m) => ({
                id: m.id,
                icon: m.icon,
                title: m.title[selectedLang] || m.title.fr || "",
                description:
                    m.description[selectedLang] || m.description.fr || "",
                enabled: m.enabled,
                descriptionVisible: methodDescVisible[m.id] !== false,
            })),
            splitPaymentEnabled,
            splitOptions: splitOptions.map((o) => ({
                id: o.id,
                icon: o.icon,
                title: o.title[selectedSplitLang] || o.title.fr || "",
                description:
                    o.description[selectedSplitLang] || o.description.fr || "",
                enabled: o.enabled,
                descriptionVisible: splitDescVisible[o.id] !== false,
            })),
        };
    }, [
        paymentEnabled,
        paymentMethods,
        splitPaymentEnabled,
        splitOptions,
        selectedLang,
        selectedSplitLang,
        methodDescVisible,
        splitDescVisible,
    ]);

    // ─── Sync preview data ──────────────────────────────
    React.useEffect(() => {
        const previewData = {
            ...buildPreviewData(),
            // Full config for Redux persistence
            selectedLang,
            selectedSplitLang,
            fullPaymentMethods: paymentMethods,
            fullSplitOptions: splitOptions,
            methodDescVisible,
            splitDescVisible,
        };
        setSharedPaiementData(previewData);

        // Broadcast to external window
        try {
            const channel = new BroadcastChannel("scan-to-preview-channel");
            channel.postMessage({ source: "paiement", ...previewData });
            channel.close();
        } catch (e) {
            // BroadcastChannel not supported
        }

        // sessionStorage for external window reload
        try {
            sessionStorage.setItem(
                "scanToPaiementPreviewData",
                JSON.stringify(previewData)
            );
        } catch (e) {
            // sessionStorage quota exceeded
        }
    }, [
        buildPreviewData,
        setSharedPaiementData,
        selectedLang,
        selectedSplitLang,
        paymentMethods,
        splitOptions,
        methodDescVisible,
        splitDescVisible,
    ]);

    // ─── Handlers: Payment Methods ───────────────────────
    const handleTogglePaymentMethod = (id: string) => {
        setPaymentMethods((prev) =>
            prev.map((m) => (m.id === id ? { ...m, enabled: !m.enabled } : m))
        );
    };

    const handlePaymentMethodTitleChange = (id: string, value: string) => {
        setPaymentMethods((prev) =>
            prev.map((m) =>
                m.id === id
                    ? { ...m, title: { ...m.title, [selectedLang]: value } }
                    : m
            )
        );
    };

    const handlePaymentMethodDescChange = (id: string, value: string) => {
        setPaymentMethods((prev) =>
            prev.map((m) =>
                m.id === id
                    ? {
                          ...m,
                          description: {
                              ...m.description,
                              [selectedLang]: value,
                          },
                      }
                    : m
            )
        );
    };

    const handleResetMethodTitle = (id: string) => {
        const def = DEFAULT_PAYMENT_METHODS.find((d) => d.id === id);
        if (!def) return;
        setPaymentMethods((prev) =>
            prev.map((m) =>
                m.id === id
                    ? {
                          ...m,
                          title: {
                              ...m.title,
                              [selectedLang]:
                                  def.title[selectedLang] || def.title.fr,
                          },
                      }
                    : m
            )
        );
    };

    const handleResetMethodDesc = (id: string) => {
        const def = DEFAULT_PAYMENT_METHODS.find((d) => d.id === id);
        if (!def) return;
        setPaymentMethods((prev) =>
            prev.map((m) =>
                m.id === id
                    ? {
                          ...m,
                          description: {
                              ...m.description,
                              [selectedLang]:
                                  def.description[selectedLang] ||
                                  def.description.fr,
                          },
                      }
                    : m
            )
        );
    };

    const handleToggleMethodDescVisible = (id: string) => {
        setMethodDescVisible((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // ─── Handlers: Split Options ─────────────────────────
    const handleToggleSplitOption = (id: string) => {
        setSplitOptions((prev) =>
            prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o))
        );
    };

    const handleSplitOptionTitleChange = (id: string, value: string) => {
        setSplitOptions((prev) =>
            prev.map((o) =>
                o.id === id
                    ? {
                          ...o,
                          title: { ...o.title, [selectedSplitLang]: value },
                      }
                    : o
            )
        );
    };

    const handleSplitOptionDescChange = (id: string, value: string) => {
        setSplitOptions((prev) =>
            prev.map((o) =>
                o.id === id
                    ? {
                          ...o,
                          description: {
                              ...o.description,
                              [selectedSplitLang]: value,
                          },
                      }
                    : o
            )
        );
    };

    const handleResetSplitTitle = (id: string) => {
        const def = DEFAULT_SPLIT_OPTIONS.find((d) => d.id === id);
        if (!def) return;
        setSplitOptions((prev) =>
            prev.map((o) =>
                o.id === id
                    ? {
                          ...o,
                          title: {
                              ...o.title,
                              [selectedSplitLang]:
                                  def.title[selectedSplitLang] || def.title.fr,
                          },
                      }
                    : o
            )
        );
    };

    const handleResetSplitDesc = (id: string) => {
        const def = DEFAULT_SPLIT_OPTIONS.find((d) => d.id === id);
        if (!def) return;
        setSplitOptions((prev) =>
            prev.map((o) =>
                o.id === id
                    ? {
                          ...o,
                          description: {
                              ...o.description,
                              [selectedSplitLang]:
                                  def.description[selectedSplitLang] ||
                                  def.description.fr,
                          },
                      }
                    : o
            )
        );
    };

    const handleToggleSplitDescVisible = (id: string) => {
        setSplitDescVisible((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <Container data-testid="scan-to-payment-container">
            <LeftSection data-testid="scan-to-payment-left-section">
                <RubricResetButton
                    section="paymentOptions"
                    onReset={resetPaymentAll}
                />
                {/* ─── Card 1: Payment Methods ─── */}
                <PaymentMethodsCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLang={selectedLang}
                    onLanguageChange={setSelectedLang}
                    paymentEnabled={paymentEnabled}
                    onPaymentEnabledToggle={() =>
                        setPaymentEnabled(!paymentEnabled)
                    }
                    paymentMethods={paymentMethods}
                    defaultPaymentMethods={DEFAULT_PAYMENT_METHODS}
                    methodDescVisible={methodDescVisible}
                    onToggleMethodDescVisible={handleToggleMethodDescVisible}
                    onTogglePaymentMethod={handleTogglePaymentMethod}
                    onPaymentMethodTitleChange={handlePaymentMethodTitleChange}
                    onPaymentMethodDescChange={handlePaymentMethodDescChange}
                    onResetMethodTitle={handleResetMethodTitle}
                    onResetMethodDesc={handleResetMethodDesc}
                    onResetAll={resetPaymentMethods}
                />

                {/* ─── Card 2: Split Payment ─── */}
                <SplitOptionsCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedSplitLang={selectedSplitLang}
                    onSplitLanguageChange={setSelectedSplitLang}
                    splitPaymentEnabled={splitPaymentEnabled}
                    onSplitPaymentEnabledToggle={() =>
                        setSplitPaymentEnabled(!splitPaymentEnabled)
                    }
                    splitOptions={splitOptions}
                    defaultSplitOptions={DEFAULT_SPLIT_OPTIONS}
                    splitDescVisible={splitDescVisible}
                    onToggleSplitDescVisible={handleToggleSplitDescVisible}
                    onToggleSplitOption={handleToggleSplitOption}
                    onSplitOptionTitleChange={handleSplitOptionTitleChange}
                    onSplitOptionDescChange={handleSplitOptionDescChange}
                    onResetSplitTitle={handleResetSplitTitle}
                    onResetSplitDesc={handleResetSplitDesc}
                    onResetAll={resetSplitOptions}
                />
            </LeftSection>
        </Container>
    );
}
